import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { AnimTap, EFonts } from 'libs/styles/styling';
import { useContext } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import styled, { CSSProperties, css } from 'styled-components/macro';

interface Props {
    icon?: {
        iconSet?: IconPrefix;
        iconName: IconName;
        iconSpin?: boolean;
        iconRotation?: number;
    };
    text?: string;
    highlighted?: boolean;
    fixedWidth?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    disabled?: boolean;
    bgColor?: string;
    iconColor?: string;

    className?: string;
    style?: CSSProperties;
    children?: JSX.Element;
}

export const ActionButton = (props: Props): JSX.Element => {
    const ctxEventView = useContext(CtxEventView);
    const anim = !props.disabled ? AnimTap : {};

    const bgColor =
        props?.bgColor ?? ctxEventView?.eventData?.eventInfo?.styling?.circle?.bgColor ?? '#C57142';
    const iconColor =
        props?.iconColor ??
        ctxEventView?.eventData?.eventInfo?.styling?.circle?.iconColor ??
        '#ffffff';

    return (
        <S_ActionButton
            className={props.className}
            style={props.style}
            $disabled={props.disabled}
            $highlighted={props.highlighted}
            $bgColor={bgColor}
            onClick={(e) => {
                if (!props.disabled) props.onClick?.(e);
            }}
            $fixedWidth={props.fixedWidth || (props.icon && !props.text)}
            {...anim}
        >
            {props.icon && (
                <S_Icon
                    $highlighted={props.highlighted}
                    icon={[props.icon.iconSet ?? 'fal', props.icon.iconName]}
                    $rotation={props.icon.iconRotation}
                    spin={props.icon.iconSpin ?? false}
                    $iconColor={iconColor}
                />
            )}
            {props.icon && props.text && <S_Space />}
            {props.text && (
                <S_Text $highlighted={props.highlighted} $iconColor={iconColor}>
                    {props.text}
                </S_Text>
            )}
            {props.children}
        </S_ActionButton>
    );
};

const S_ActionButton = styled(motion.div)<{
    $fixedWidth: boolean;
    $disabled: boolean;
    $bgColor: string;
    $highlighted: boolean;
}>`
    flex-shrink: 0;
    min-height: 32px;
    font-size: 13px;
    display: flex;
    height: fit-content;
    justify-content: center;
    align-items: center;
    border-radius: 100px;
    background: #fff;
    box-shadow: 5px 5px 15px 0px rgba(0, 0, 0, 0.05);
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    user-select: none;
    height: 42px;
    box-sizing: border-box;
    ${(props) => {
        if (props.$fixedWidth) {
            return css`
                width: 40px;
            `;
        } else {
            return css`
                width: fit-content;
                padding: 8px 15px;
            `;
        }
    }}
    ${(props) => {
        if (props.$highlighted) {
            return css`
                background-color: ${props.$bgColor};
            `;
        } else {
            return css`
                &:hover {
                    background-color: #f1f2f4;
                    cursor: pointer;
                }
            `;
        }
    }}
`;

const S_Icon = styled(FontAwesomeIcon)<{
    $rotation?: number;
    $highlighted: boolean;
    $iconColor: string;
}>`
    color: ${(props) => (props.$highlighted ? props.$iconColor : '#3a3a3a')};
    font-size: 17px;
    width: 20px;
    ${(props) => {
        if (props.$rotation) {
            return css`
                transform: rotate(${props.$rotation}deg);
            `;
        }
    }}
`;

const S_Text = styled.div<{ $highlighted: boolean; $iconColor: string }>`
    color: ${(props) => (props.$highlighted ? props.$iconColor : '#3a3a3a')};
    font-family: ${EFonts.InterMedium};
    font-size: 13px;
`;

const S_Space = styled.div`
    width: 10px;
`;
